import { Button, Col, DatePicker, Form, Grid, Input, message, Modal, Row } from 'antd';
import React, { useState } from 'react';
import '../../../../css/entity-style.css';
import dayjs from 'dayjs';
import { PageHeader } from '@ant-design/pro-layout';
import Title from 'antd/es/typography/Title';
import { FormatDate } from '../../../../constants';

interface VerificationFormProps {
  generatePdf: (formData: VerificationFormData) => Promise<File>;
}

export interface VerificationFormData {
  applicantId: string;
  caseId: string;
  entityId: string;
  firstName: string;
  lastName: string;
  nameOnDocument: string;
  dateOfBirth: string;
  email: string;
}

export const VerificationForm: React.FC<VerificationFormProps> = (props) => {
  const DATE_FORMAT = 'DD-MMM-YYYY';
  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs;
  const [form] = Form.useForm<VerificationFormData>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [applicantIdDisabled, setApplicantIdDisabled] = useState(false);

  const handleValuesChange: (changedValues: Partial<VerificationFormData>) => void = (changedValues) => {
    if ('applicantId' in changedValues) {
      setEmailDisabled(!!changedValues.applicantId);
    }
    if ('email' in changedValues) {
      setApplicantIdDisabled(!!changedValues.email);
    }
  };

  const handleModalClose = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setIsModalVisible(false);
    form.resetFields();
  };

  const onSubmit = async (values: any) => {
    try {
      const verificationFormObj: VerificationFormData = {
        applicantId: values.applicantId,
        caseId: values.caseId,
        entityId: values.entityId,
        firstName: values.firstName,
        lastName: values.lastName,
        nameOnDocument: values.nameOnDocument,
        dateOfBirth: FormatDate(values.dateOfBirth),
        email: values.email
      };
      localStorage.setItem('verificationFormData', JSON.stringify(verificationFormObj));
      message.success('Form submitted successfully!');
      const generatedPdf = await props.generatePdf(verificationFormObj);
      const pdfBlob = new Blob([generatedPdf], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(pdfUrl);
      setIsModalVisible(true);
      setEmailDisabled(false);
      setApplicantIdDisabled(false);
    } catch (error) {
      message.error('An error occurred while submitting the form.');
      console.error(error);
    }
  };

  const emailPattern =
    /^(?:"[a-zA-Z0-9!#$%&'*+/=?^_`{|}~\s-]+"|[a-zA-Z0-9!#$%&'*+/=?^_`{|}~](?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~\-]*[a-zA-Z0-9!#$%&'*+/=?^_`{|}~])?)(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~\-](?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~\-]*[a-zA-Z0-9!#$%&'*+/=?^_`{|}~])?)*@[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+$/;

  return (
    <>
      <PageHeader>
        <div className="flex flex-col" style={{ marginBottom: '30px' }}>
          <div id="leftTitle" style={{ display: 'inline', float: 'left' }}>
            <Title level={3} style={{ display: 'inline' }}>
              Verification Form
            </Title>
          </div>
        </div>
      </PageHeader>
      <div className="flex flex-col">
        <div className="flex justify-between items-center">
          <Form
            form={form}
            onFinish={(values) => {
              onSubmit(values);
            }}
            onValuesChange={handleValuesChange}
            onFinishFailed={(err) => {
              console.log('err', err);
            }}
            layout="vertical"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'right',
              justifyContent: 'end'
            }}
          >
            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item label="Applicant ID" name="applicantId" rules={[{ min: 24, message: 'Applicant ID must be at least 24 characters long' }]}>
                  <Input placeholder="Enter Applicant ID" disabled={applicantIdDisabled} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Case ID" name="caseId" rules={[{ min: 24, message: 'Case ID must be at least 24 characters long' }]}>
                  <Input placeholder="Enter Case ID" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={20}>
              <Col xs={24} sm={24}>
                <Form.Item label="Entity ID" name="entityId" rules={[{ min: 24, message: 'Entity ID must be at least 24 characters long' }]}>
                  <Input placeholder="Enter Entity ID" disabled={applicantIdDisabled} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: 'First Name is required' }]}>
                  <Input placeholder="Enter First Name" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: 'Last Name is required' }]}>
                  <Input placeholder="Enter Last Name" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={20}>
              <Col xs={24} sm={24}>
                <Form.Item label="Name on Document" name="nameOnDocument" rules={[{ required: true, message: 'Name on Document is required' }]}>
                  <Input placeholder="Name on Document" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={20}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Date Of Birth"
                  style={{ width: '100%' }}
                  name="dateOfBirth"
                  valuePropName="value"
                  rules={[{ required: true, message: 'Date Of Birth is Required' }]}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : ''
                  })}
                >
                  <DatePicker maxDate={dayjs().endOf('day')} format={(value) => value.format(DATE_FORMAT).toUpperCase()} style={{ width: isMobile ? '100%' : 'auto' }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Email Address"
                  name="email"
                  rules={[{ pattern: emailPattern, message: 'Email must contain alphanumeric characters followed by "@" and a valid domain' }]}
                >
                  <Input placeholder="Email Address" disabled={emailDisabled} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={20}>
              <Col xs={24} sm={24}>
                <Form.Item>
                  <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
                    Generate Verification Form
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
        <div>
          <Modal open={isModalVisible} footer={null} onCancel={handleModalClose} width="80%" height="150%" style={{ top: 20 }}>
            <embed src={pdfUrl ?? undefined} type="application/pdf" width="100%" height="1000px" title="Generated PDF" />
          </Modal>
        </div>
      </div>
    </>
  );
};
